// lists.js
'use strict';
import { fromJS } from 'immutable';
import {
  ADD_LIST_ITEMS,
  LOGOUT,
  REMOVE_LIST_ITEMS,
  REMOVE_LIVE_STREAM_NOTICES,
  SET_LIST_ITEMS,
  RESTORE_LIST_ITEMS,
  PUSH_LIVESTREAM_CHAT_MESSAGES,
  // -- PLOP_PREPEND_REDUCER_ACTION_TYPE --
} from '../ActionTypes.js';

export const defaultState = fromJS({
  job: {},
});

/**
 * Lists.
 * @module reducer/lists
 */
const lists = (state = defaultState, action) => {
  switch (action.type) {
    case ADD_LIST_ITEMS:
      return _ADD_LIST_ITEMS(action.payload)(state);
    case REMOVE_LIST_ITEMS:
      return _REMOVE_LIST_ITEMS(action.payload)(state);
    case REMOVE_LIVE_STREAM_NOTICES:
      return _REMOVE_LIVE_STREAM_NOTICES(action.payload)(state);
    case LOGOUT:
      return _LOGOUT(action.payload)(state);
    case SET_LIST_ITEMS:
      return _SET_LIST_ITEMS(action.payload)(state);
    case RESTORE_LIST_ITEMS:
      return _RESTORE_LIST_ITEMS(action.payload)(state);
    case PUSH_LIVESTREAM_CHAT_MESSAGES:
      return _PUSH_LIVESTREAM_CHAT_MESSAGES(action.payload)(state);
    // -- PLOP_PREPEND_REDUCER_SWITCH_CASE --
    default:
      return state;
  }
};

/**
 * Add list items
 * @kind reducer/actionType
 * @name ADD_LIST_ITEMS
 * @param {array} {selectPath} - list select path
 * @param {array} [{itemIds}=[]] - list item ids
 * @param {number} [{totalCount}] - list total count
 * @param {number} [{nextPage}] - list next page
 * @param {string} [{nextPageUrl}] - cursor based pagination next page url
 * @param {number} [{previousPage}] - list previous page
 * @param {number} [{lastPage}] - list last page
 * @param {object} [{pages}] - pages
 * @param {boolean} [{isAllowSameId}] - is allow same id.
 * @return {Immutable.Map} New state
 */
const _ADD_LIST_ITEMS =
  ({
    selectPath = [`unknown-${Math.random()}`],
    itemIds = [],
    totalCount,
    nextPage,
    nextPageUrl,
    previousPage,
    lastPage,
    pages = {},
    isAllowSameId,
  }) =>
  state => {
    const currentIds = state.getIn([...selectPath, 'itemIds']) || fromJS([]);
    return state
      .mergeDeepIn(selectPath, {
        totalCount,
        nextPage,
        nextPageUrl,
        previousPage,
        lastPage,
        pages,
      })
      .setIn(
        [...selectPath, 'itemIds'],
        currentIds.concat(
          itemIds.filter(itemId =>
            isAllowSameId ? true : !currentIds.includes(itemId)
          )
        )
      );
  };

/**
 * Logout
 * @kind reducer/actionType
 * @name LOGOUT
 * @return {Immutable.Map} New state
 */
const _LOGOUT = () => state => {
  return defaultState.mergeDeep({
    job: state.get('job'),
  });
};

/**
 * Remove list item
 * @kind reducer/actionType
 * @name REMOVE_LIST_ITEMS
 * @param {array} {selectPath} - list select path
 * @param {array} {itemIds} - list item ids
 * @param {number} [{totalCount}] - list total count
 * @param {number} [{nextPage}] - list next page
 * @param {string} [{nextPageUrl}] - cursor based pagination next page url
 * @param {number} [{previousPage}] - list previous page
 * @param {number} [{lastPage}] - list last page
 * @return {Immutable.Map} New state
 */
const _REMOVE_LIST_ITEMS =
  ({
    selectPath = [`unknown-${Math.random()}`],
    itemIds = [],
    totalCount,
    nextPage,
    nextPageUrl,
    previousPage,
    lastPage,
  }) =>
  state => {
    const currentIds = state.getIn([...selectPath, 'itemIds']) || fromJS([]);
    const getShouldRemove = id => itemIds.includes(id);
    const pages = (state.getIn([...selectPath, 'pages']) || fromJS({})).map(
      ids => {
        const newIds = (ids || fromJS([])).filter(id => !getShouldRemove(id));
        return newIds;
      }
    );
    return state
      .mergeDeepIn(selectPath, {
        totalCount,
        nextPage,
        nextPageUrl,
        previousPage,
        lastPage,
      })
      .setIn([...selectPath, 'pages'], pages)
      .setIn(
        [...selectPath, 'itemIds'],
        currentIds.filter(id => itemIds.every(itemId => id !== itemId))
      );
  };

/**
 * Remove livestream notices — composite action answered by both the lists
 * reducer (queue itemIds, here) and the liveStream reducer (notice bodies),
 * so retiring a notice costs a single dispatch.
 * @kind reducer/actionType
 * @name REMOVE_LIVE_STREAM_NOTICES
 * @param {string} {streamId} - stream id
 * @param {string} {noticeType} - stream notice type
 * @param {array} {noticeIds} - notice ids to remove
 * @return {Immutable.Map} New state
 */
const _REMOVE_LIVE_STREAM_NOTICES =
  ({ streamId, noticeType, noticeIds = [] }) =>
  state => {
    return _REMOVE_LIST_ITEMS({
      selectPath: ['liveStream', streamId, noticeType],
      itemIds: noticeIds,
    })(state);
  };

/**
 * Set list items
 * @kind reducer/actionType
 * @name SET_LIST_ITEMS
 * @param {array} {selectPath} - list select path
 * @param {array} {itemIds} - list item ids
 * @param {number} [{totalCount}] - list total count
 * @param {number} [{nextPage}] - list next page
 * @param {string} [{nextPageUrl}] - cursor based pagination next page url
 * @param {number} [{previousPage}] - list previous page
 * @param {number} [{lastPage}] - list last page
 * @param {object} [{pages}] - pages
 * @return {Immutable.Map} New state
 */
const _SET_LIST_ITEMS =
  ({
    selectPath = [`unknown-${Math.random()}`],
    itemIds = [],
    totalCount,
    nextPage,
    nextPageUrl,
    previousPage,
    lastPage,
    pages = {},
  }) =>
  state => {
    const ids = fromJS(itemIds);
    return state
      .mergeDeepIn(selectPath, {
        totalCount,
        nextPage,
        nextPageUrl,
        previousPage,
        lastPage,
      })
      .setIn([...selectPath, 'itemIds'], ids)
      .setIn([...selectPath, 'pages'], fromJS(pages));
  };

/**
 * Restore list item
 * @kind reducer/actionType
 * @name RESTORE_LIST_ITEMS
 * @param {object} listData - operation data
 * @return {Immutable.Map} New state
 */
const _RESTORE_LIST_ITEMS = listData => state => {
  return state.mergeDeep(listData);
};

/**
 * Push live stream chat messages: replace the chat display window and the
 * larger body retention window with the trimmed item ids computed by the
 * action (livestreamChat reducer handles the message-body side of this
 * action).
 * @kind reducer/actionType
 * @name PUSH_LIVESTREAM_CHAT_MESSAGES
 * @param {array} {selectPath} - chat display window list select path
 * @param {array} {bodySelectPath} - body retention window list select path
 * @param {array} {itemIds} - chat display window item ids after trimming
 * @param {array} {bodyItemIds} - body retention window item ids
 * @return {Immutable.Map} New state
 */
const _PUSH_LIVESTREAM_CHAT_MESSAGES =
  ({ selectPath, bodySelectPath, itemIds = [], bodyItemIds = [] }) =>
  state =>
    state
      .setIn([...selectPath, 'itemIds'], fromJS(itemIds))
      .setIn([...bodySelectPath, 'itemIds'], fromJS(bodyItemIds));

// -- PLOP_PREPEND_REDUCER_ACTION_HANDLER --

export default lists;
